"use client"

import { AlbumStripAdWrapper } from "@/components/ads/AlbumStripAdWrapper"
import type { BooruPost } from "@/lib/booru-api"
import { cn } from "@/lib/utils"
import Image from "next/image"
import type React from "react"

// Extracted from components/booru-detail-modal.tsx (pure move: same JSX, same behaviour).
interface AlbumThumbnailStripProps {
  albumPages: string[]
  albumStripRef: React.RefObject<HTMLDivElement | null>
  albumThumbnailsRef: React.RefObject<HTMLDivElement | null>
  currentAlbumPage: number
  getMediaUrl: (url?: string | null, isFullSize?: boolean) => string
  handleAlbumAdSlotClick: (adSlot: number) => void
  handleAlbumPageClick: (pageIndex: number) => void
  hideUI: boolean
  isAlbum: boolean
  post: BooruPost
  viewingAdSlot: number | null
}

export function AlbumThumbnailStrip({
  albumPages,
  albumStripRef,
  albumThumbnailsRef,
  currentAlbumPage,
  getMediaUrl,
  handleAlbumAdSlotClick,
  handleAlbumPageClick,
  hideUI,
  isAlbum,
  post,
  viewingAdSlot,
}: AlbumThumbnailStripProps) {

  return (
    <>
      {!hideUI && isAlbum && (
                <div
                  ref={albumStripRef}
                  className="absolute bottom-0 left-0 right-0 bg-black/80 px-2 z-30 leading-4 py-1.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    ref={albumThumbnailsRef}
                    className="flex gap-1.5 px-1.5 py-1 overflow-x-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent"
                  >
                    <AlbumStripAdWrapper
                      totalPages={albumPages.length}
                      currentPage={currentAlbumPage}
                      onAdSlotClick={handleAlbumAdSlotClick}
                      activeAdSlot={viewingAdSlot}
                      renderThumbnail={(index) => {
                        const pageUrl = albumPages[index]
                        const isPageVideo = pageUrl?.match(/\.(webm|mp4|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)(\?|#|$)/i)
                        return (
                          <button
                            key={index}
                            data-real-page={index}
                            onClick={() => handleAlbumPageClick(index)}
                            className={cn(
                              "relative flex-shrink-0 w-12 h-12 rounded overflow-hidden border-2 transition-all",
                              currentAlbumPage === index
                                ? "border-purple-500 ring-2 ring-purple-400 scale-110"
                                : "border-transparent hover:border-white/50",
                            )}
                          >
                            {isPageVideo ? (
                              <video
                                src={getMediaUrl(pageUrl)}
                                poster={post.previewUrl ? getMediaUrl(post.previewUrl) : undefined}
                                className="w-full h-full object-cover"
                                preload="metadata"
                                muted
                                playsInline
                                onLoadedMetadata={(e) => {
                                  // Same black-thumbnail issue as the main
                                  // player — force a real frame to paint.
                                  if (e.currentTarget.currentTime === 0) {
                                    e.currentTarget.currentTime = 0.1
                                  }
                                }}
                              />
                            ) : (
                              <Image
                                src={getMediaUrl(pageUrl) || "/placeholder.svg"}
                                alt={`Page ${index + 1}`}
                                fill
                                className="object-cover"
                                unoptimized
                              />
                            )}

                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[9px] text-center">
                              {index + 1}
                            </div>
                          </button>
                        )
                      }}
                    />
                  </div>
                </div>
              )}
    </>
  )
}
